// Reusable admin date-range filter. Renders the preset chip strip + custom
// From/To inputs. Emits the active ISO range + a friendly label whenever the
// admin picks a preset or types a custom date — parent uses both to fetch
// data and to render copy like "Top customers · This month".
import { useEffect, useMemo, useState } from 'react'

export type RangeKey = 'today' | '7d' | '30d' | 'mtd' | 'last_month' | 'ytd' | 'all'
export type Range = RangeKey | 'custom'

export interface RangeValue {
  fromISO: string
  toISO: string
  label: string
}

const RANGES: Array<{ key: RangeKey; label: string }> = [
  { key: 'today',      label: 'Today' },
  { key: '7d',         label: '7 days' },
  { key: '30d',        label: '30 days' },
  { key: 'mtd',        label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'ytd',        label: 'Year to date' },
  { key: 'all',        label: 'All time' },
]

function rangeFor(key: RangeKey): { from: string; to: string } {
  const now = new Date()
  const to  = new Date(now); to.setHours(23, 59, 59, 999)
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0)
  switch (key) {
    case 'today': return { from: startOfToday.toISOString(),                       to: to.toISOString() }
    case '7d':    return { from: new Date(now.getTime() - 7  * 86400000).toISOString(), to: to.toISOString() }
    case '30d':   return { from: new Date(now.getTime() - 30 * 86400000).toISOString(), to: to.toISOString() }
    case 'mtd':   return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), to: to.toISOString() }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const end   = new Date(now.getFullYear(), now.getMonth(), 0); end.setHours(23, 59, 59, 999)
      return { from: start.toISOString(), to: end.toISOString() }
    }
    case 'ytd':   return { from: new Date(now.getFullYear(), 0, 1).toISOString(), to: to.toISOString() }
    case 'all':   return { from: new Date(2020, 0, 1).toISOString(),               to: to.toISOString() }
  }
}

function isoDate(s: string) { return s.slice(0, 10) }

export default function DateRangeFilter({
  defaultKey = '30d',
  onChange,
}: {
  defaultKey?: RangeKey
  onChange: (value: RangeValue) => void
}) {
  const [active, setActive] = useState<Range>(defaultKey)
  const initial = rangeFor(defaultKey)
  const [fromDate, setFromDate] = useState<string>(isoDate(initial.from))
  const [toDate,   setToDate]   = useState<string>(isoDate(initial.to))

  const value: RangeValue = useMemo(() => {
    if (active === 'custom') {
      const f = new Date(fromDate + 'T00:00:00')
      const t = new Date(toDate   + 'T23:59:59')
      return { fromISO: f.toISOString(), toISO: t.toISOString(), label: `${fromDate} to ${toDate}` }
    }
    const r = rangeFor(active)
    return { fromISO: r.from, toISO: r.to, label: RANGES.find((x) => x.key === active)?.label ?? '' }
  }, [active, fromDate, toDate])

  // Push value upward on every change. Parent owns the fetch.
  useEffect(() => { onChange(value) /* eslint-disable-next-line */ }, [value.fromISO, value.toISO, value.label])

  function pick(k: RangeKey) {
    const r = rangeFor(k)
    setActive(k)
    setFromDate(isoDate(r.from))
    setToDate(isoDate(r.to))
  }

  return (
    <div className="card mb-6 flex flex-wrap items-center gap-3">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mr-1">Range</div>
      <div className="flex flex-wrap gap-1.5">
        {RANGES.map((r) => (
          <button key={r.key} onClick={() => pick(r.key)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors whitespace-nowrap ' +
              (active === r.key ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <label className="text-[10px] uppercase tracking-wider font-bold text-ink-3">From</label>
        <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setActive('custom') }}
          className="input !w-auto !py-1 text-xs" />
        <label className="text-[10px] uppercase tracking-wider font-bold text-ink-3">To</label>
        <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setActive('custom') }}
          className="input !w-auto !py-1 text-xs" />
      </div>
    </div>
  )
}
